import type { ReactNode } from 'react';
import type { PublicPost } from '@openpeepshq/common/types';
import { cn } from '@openpeepshq/react-ui';
import { usePostViewRef } from '../../lib/postViewCounter';
import { isUnreadFeedActivityForViewer } from '../../lib/postUnread';
import { useCurrentProfile } from '../layout/IdentityContext';

import { FeedPostContent } from './FeedPostContent';
import { PostInfoHeader } from './pieces/PostInfoHeader';
import { PostReactionHeader } from './pieces/PostReactionHeader';
import { FeedPostStats } from './pieces/FeedPostStats';
import { PostActions } from './pieces/PostActions';
import { CompactReplyParent } from './CompactReplyParent';
import { UnreadPostIndicator } from './pieces/UnreadPostIndicator';
import { FeedThreadPreview } from './pieces/FeedThreadPreview';

export interface FeedPostProps {
  post: PublicPost;
  deleteCallback?: () => void;
  noReactionHeader?: boolean;
  inGroup?: boolean;
  showReplyTo?: boolean;
  /** Optional override for the body (used when threading). */
  content?: ReactNode;
  className?: string;
}

/**
 * Single post card for the chronological feed:
 * shows a small reaction/reply/group banner, the author header, the body,
 * stats and actions. The body component dispatches on `post.type`.
 */
export function FeedPost({
  post,
  deleteCallback,
  noReactionHeader = false,
  inGroup = false,
  showReplyTo = false,
  content,
  className,
}: FeedPostProps) {
  const me = useCurrentProfile();
  const displayedPost: PublicPost = post.repost ?? post;
  const isUnread = isUnreadFeedActivityForViewer(post, me?.id);
  const postViewRef = usePostViewRef(post.id, {
    groupId: post.groupId,
    adjustUnread: isUnread,
  });
  const hasReactionHeader =
    !noReactionHeader &&
    (!!post.repost || !!post.inReplyToId || (!!post.groupId && !inGroup));

  const showsReplyTo = !!(showReplyTo && displayedPost.replyTo);
  const showThreadPreview =
    !noReactionHeader &&
    !showsReplyTo &&
    !displayedPost.inReplyToId &&
    ((displayedPost.latestReplies?.length ?? 0) > 0 ||
      displayedPost.latestRepliesHasMore ||
      (displayedPost.replyCount ?? 0) > 0);

  const hasStats = !!(
    displayedPost?.repostCount ||
    displayedPost?.reactions?.length ||
    displayedPost?.replyCount
  );

  return (
    <article
      ref={postViewRef}
      className={cn(
        'bg-background border-border relative min-w-0 border-b p-4',
        className,
      )}
    >
      <UnreadPostIndicator show={isUnread} variant="corner" />
      {hasReactionHeader && (
        <PostReactionHeader
          post={post}
          inGroup={inGroup}
          deleteCallback={deleteCallback}
        />
      )}

      {showsReplyTo && displayedPost.replyTo && (
        <CompactReplyParent post={displayedPost.replyTo as PublicPost} />
      )}

      <div className="relative">
        <PostInfoHeader
          post={displayedPost}
          showMenu={!hasReactionHeader}
          deleteCallback={deleteCallback}
        />
        <div className="min-w-0 pb-2">
          {content ?? <FeedPostContent post={displayedPost} />}
        </div>
        {hasStats && <FeedPostStats post={displayedPost} />}
      </div>

      <PostActions post={displayedPost} />
      {showThreadPreview ? <FeedThreadPreview post={displayedPost} /> : null}
    </article>
  );
}
